import { computed, watch, type Ref } from 'vue'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { ENDPOINTS } from '@/api'
import { ApiClient } from '@/utils/api-client'
import { normalizeGallery } from '@/views/profile/achievements/adapters'

export const achievementKeys = { account: (id: string) => ['user', 'achievements', id] as const, list: (id: string, category: 'cups' | 'medals') => ['user', 'achievements', id, 'fa', category] as const }
export async function getAchievements(category: 'cups' | 'medals', signal?: AbortSignal) {
  const response = await ApiClient.get<unknown>(category === 'cups' ? ENDPOINTS.User.Cups : ENDPOINTS.User.Medals, { signal, suppressGlobalErrorRedirects: true, retainUnauthorizedRedirect: true })
  return normalizeGallery(response)
}
export function useAchievements(account: Ref<string>, category: 'cups' | 'medals') {
  return useQuery({ queryKey: computed(() => achievementKeys.list(account.value, category)), queryFn: ({ signal }) => getAchievements(category, signal), enabled: computed(() => !!account.value), staleTime: 0, gcTime: 0, retry: false, refetchOnWindowFocus: true, refetchOnReconnect: true })
}
export function useAchievementAccountCleanup(account: Ref<string>) {
  const client = useQueryClient()
  watch(account, (_, previous) => {
    if (previous) { const queryKey = achievementKeys.account(previous); void client.cancelQueries({ queryKey }); client.removeQueries({ queryKey }) }
  }, { flush: 'sync' })
  // Remove responses cached by older versions of the generic API runtime route.
  if (typeof caches !== 'undefined') void caches.open('api-cache').then(async cache => {
    for (const request of await cache.keys()) if (/\/v3\/user\/(cups|medals)\/?$/.test(new URL(request.url).pathname)) await cache.delete(request)
  }).catch(() => {})
}
